// Enhanced theme service with Figma integration
import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { figmaTokens, darkThemeTokens, generateCssVariables } from '../theme/tokens/figma-tokens';
import { PrimeNGThemeLoaderService } from './primeng-theme-loader.service';

export type ThemeMode = 'light' | 'dark' | 'auto';

export interface ThemeConfig {
  name: string;
  displayName: string;
  tokens: Record<string, any>;
  cssVariables: Record<string, string>;
  isDark: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  // Reactive state
  private currentTheme = signal<ThemeMode>('light');
  private isTransitioning = signal<boolean>(false);
  private systemPrefersDark = signal<boolean>(false);
  private availableThemes = signal<ThemeConfig[]>([]);
  
  // Public getters
  readonly theme = this.currentTheme.asReadonly();
  readonly transitioning = this.isTransitioning.asReadonly();
  readonly effectiveTheme = computed(() => {
    const theme = this.currentTheme();
    if (theme === 'auto') {
      return this.systemPrefersDark() ? 'dark' : 'light';
    }
    return theme;
  });

  // Inject PrimeNG theme loader
  private primeNGThemeLoader = inject(PrimeNGThemeLoaderService);

  constructor() {
    this.initializeThemes();
    this.initializeSystemPreference();
    this.initializeFromStorage();

    // React to effective theme changes
    effect(() => {
      this.applyTheme(this.effectiveTheme());
    });
  }

  /**
   * Initialize available themes
   */
  private initializeThemes(): void {
    const themes: ThemeConfig[] = [
      {
        name: 'light',
        displayName: 'Light',
        tokens: figmaTokens,
        cssVariables: generateCssVariables(figmaTokens),
        isDark: false
      },
      {
        name: 'dark',
        displayName: 'Dark',
        tokens: { ...figmaTokens, colors: { ...figmaTokens.colors, ...darkThemeTokens.colors } },
        cssVariables: generateCssVariables({ ...figmaTokens, colors: { ...figmaTokens.colors, ...darkThemeTokens.colors } }),
        isDark: true
      }
    ];
    
    this.availableThemes.set(themes);
  }

  /**
   * Initialize system preference detection
   */
  private initializeSystemPreference(): void {
    // Check initial system preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemPrefersDark.set(mediaQuery.matches);
    
    // Listen for system preference changes
    mediaQuery.addEventListener('change', (e) => {
      this.systemPrefersDark.set(e.matches);
    });
  }

  /**
   * Initialize theme from localStorage
   */
  private initializeFromStorage(): void {
    try {
      const savedTheme = localStorage.getItem('tibss_theme') as ThemeMode;
      if (savedTheme && this.isValidTheme(savedTheme)) {
        this.currentTheme.set(savedTheme);
      } else {
        this.currentTheme.set('auto');
      }
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
      this.currentTheme.set('auto');
    }
  }

  /**
   * Set the current theme
   */
  async setTheme(theme: ThemeMode, options: { animate?: boolean } = {}): Promise<void> {
    const { animate = true } = options;
    
    if (!this.isValidTheme(theme)) {
      console.warn(`Invalid theme: ${theme}`);
      return;
    }

    if (animate) {
      await this.prepareTransition();
    }

    this.currentTheme.set(theme);
    this.saveThemeToStorage(theme);

    if (animate) {
      await this.completeTransition();
    }
  }

  /**
   * Toggle between light and dark themes
   */
  async toggleTheme(): Promise<void> {
    const current = this.currentTheme();
    let nextTheme: ThemeMode;
    
    if (current === 'auto') {
      nextTheme = this.systemPrefersDark() ? 'light' : 'dark';
    } else {
      nextTheme = current === 'light' ? 'dark' : 'light';
    }
    
    await this.setTheme(nextTheme);
  }

  /**
   * Get the current theme configuration
   */
  getCurrentThemeConfig(): ThemeConfig | undefined {
    const effectiveTheme = this.effectiveTheme();
    return this.availableThemes().find(theme => theme.name === effectiveTheme);
  }

  /**
   * Get all available themes
   */
  getAvailableThemes(): ThemeConfig[] {
    return this.availableThemes();
  }

  /**
   * Get a specific token value
   */
  getToken(path: string): string {
    const config = this.getCurrentThemeConfig();
    if (!config) return '';
    
    return this.getNestedValue(config.tokens, path) || '';
  }

  /**
   * Check if a theme name is valid
   */
  private isValidTheme(theme: string): theme is ThemeMode {
    const validThemes = ['light', 'dark', 'auto'];
    return validThemes.includes(theme);
  }

  /**
   * Apply theme to the DOM
   */
  private applyTheme(themeName: string): void {
    const config = this.availableThemes().find(t => t.name === themeName);
    if (!config) return;

    const root = document.documentElement;

    // Set theme attribute
    root.setAttribute('data-theme', themeName);

    // Set dark/light class for compatibility
    if (config.isDark) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    // Apply CSS custom properties
    Object.entries(config.cssVariables).forEach(([property, value]) => {
      root.style.setProperty(`--${property}`, value);
    });

    // Load corresponding PrimeNG theme
    this.primeNGThemeLoader.loadTheme(config.isDark);

    // Update meta theme-color for mobile browsers
    this.updateMetaThemeColor(config);
  }

  /**
   * Prepare for theme transition
   */
  private async prepareTransition(): Promise<void> {
    this.isTransitioning.set(true);
    
    const root = document.documentElement;
    root.style.setProperty('--theme-transition', 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)');
    
    // Allow styles to be applied
    await new Promise(resolve => requestAnimationFrame(resolve));
  }

  /**
   * Complete theme transition
   */
  private async completeTransition(): Promise<void> {
    return new Promise(resolve => {
      setTimeout(() => {
        document.documentElement.style.removeProperty('--theme-transition');
        this.isTransitioning.set(false);
        resolve();
      }, 300);
    });
  }

  /**
   * Save theme preference to localStorage
   */
  private saveThemeToStorage(theme: ThemeMode): void {
    try {
      localStorage.setItem('tibss_theme', theme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }

  /**
   * Update meta theme-color for mobile browsers
   */
  private updateMetaThemeColor(config: ThemeConfig): void {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const themeColor = config.isDark ? '#1a1a1a' : '#ffffff';
      metaThemeColor.setAttribute('content', themeColor);
    }
  }

  /**
   * Get nested object value by path
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }
}
